import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { Moon, Sun, Bell, Shield, User, Palette } from 'lucide-react';
import {
  StyledSettingsContainer,
  StyledTitle,
  StyledSection,
  StyledSectionTitle,
  StyledSettingCard,
  StyledSettingRow,
  StyledFormRow,
  StyledSettingInfo,
  StyledSettingLabel,
  StyledSettingDescription,
  StyledToggleButton,
  StyledSelect,
  StyledInput,
  StyledButtonGroup,
} from './SettingsPage.styles';

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const authContext = useContext(AuthContext);
  const themeContext = useContext(ThemeContext);

  const user = authContext?.user;
  const theme = themeContext?.isDarkMode ? 'dark' : 'light';
  const toggleTheme = themeContext?.toggleTheme;

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [preferences, setPreferences] = useState({
    language: i18n.language,
    currency: 'USD',
    notifications: {
      email: true,
      push: true,
      achievements: true,
      bills: true,
    },
  });

  // Initialize profile data when user is loaded
  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });

      // Initialize preferences from user data if available
      if (user.preferences) {
        setPreferences((prev) => ({
          ...prev,
          language: user.preferences?.language || i18n.language,
          currency: user.preferences?.currency || 'USD',
          notifications: user.preferences?.notifications || prev.notifications,
        }));
      }
    }
  }, [user, i18n.language]);

  const handleProfileChange = (field: string, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationToggle = (type: string) => {
    setPreferences((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type as keyof typeof prev.notifications],
      },
    }));
  };

  const handleLanguageChange = (language: string) => {
    setPreferences((prev) => ({ ...prev, language }));
    i18n.changeLanguage(language);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Saving settings:', { profile, preferences });
  };

  return (
    <StyledSettingsContainer>
      <StyledTitle>{t('settings.title')}</StyledTitle>

      <StyledSection>
        <StyledSectionTitle>
          <User size={20} />
          {t('settings.profile')}
        </StyledSectionTitle>
        <StyledSettingCard>
          <StyledFormRow>
            <StyledSettingLabel>{t('auth.firstName')}</StyledSettingLabel>
            <StyledInput
              type='text'
              value={profile.firstName}
              onChange={(e) => handleProfileChange('firstName', e.target.value)}
              placeholder={t('auth.firstName')}
            />
          </StyledFormRow>
          <StyledFormRow>
            <StyledSettingLabel>{t('auth.lastName')}</StyledSettingLabel>
            <StyledInput
              type='text'
              value={profile.lastName}
              onChange={(e) => handleProfileChange('lastName', e.target.value)}
              placeholder={t('auth.lastName')}
            />
          </StyledFormRow>
          <StyledFormRow>
            <StyledSettingLabel>{t('auth.email')}</StyledSettingLabel>
            <StyledInput type='email' value={profile.email} $disabled={true} disabled placeholder={t('auth.email')} />
          </StyledFormRow>
        </StyledSettingCard>
      </StyledSection>

      <StyledSection>
        <StyledSectionTitle>
          <Palette size={20} />
          {t('settings.preferences')}
        </StyledSectionTitle>
        <StyledSettingCard>
          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>{t('settings.theme')}</StyledSettingLabel>
              <StyledSettingDescription>
                {theme === 'light' ? t('settings.lightMode') : t('settings.darkMode')}
              </StyledSettingDescription>
            </StyledSettingInfo>
            <StyledToggleButton $active={theme === 'dark'} onClick={toggleTheme}>
              {theme === 'light' ? <Sun size={12} /> : <Moon size={12} />}
            </StyledToggleButton>
          </StyledSettingRow>{' '}
          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>{t('settings.language')}</StyledSettingLabel>
              <StyledSettingDescription>{t('settings.languageDescription')}</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledSelect value={preferences.language} onChange={(e) => handleLanguageChange(e.target.value)}>
              <option value='en'>English</option>
              <option value='es'>Español</option>
              <option value='fr'>Français</option>
              <option value='de'>Deutsch</option>
              <option value='ja'>日本語</option>
              <option value='he'>עברית</option>
            </StyledSelect>
          </StyledSettingRow>{' '}
          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>{t('settings.currency')}</StyledSettingLabel>
              <StyledSettingDescription>{t('settings.currencyDescription')}</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledSelect
              value={preferences.currency}
              onChange={(e) => setPreferences((prev) => ({ ...prev, currency: e.target.value }))}
            >
              <option value='USD'>USD ($)</option>
              <option value='EUR'>EUR (€)</option>
              <option value='GBP'>GBP (£)</option>
              <option value='JPY'>JPY (¥)</option>
            </StyledSelect>
          </StyledSettingRow>
        </StyledSettingCard>
      </StyledSection>

      <StyledSection>
        <StyledSectionTitle>
          <Bell size={20} />
          {t('settings.notifications')}
        </StyledSectionTitle>
        <StyledSettingCard>
          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Email Notifications</StyledSettingLabel>
              <StyledSettingDescription>Receive notifications via email</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledToggleButton
              $active={preferences.notifications.email}
              onClick={() => handleNotificationToggle('email')}
            />
          </StyledSettingRow>

          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Push Notifications</StyledSettingLabel>
              <StyledSettingDescription>Receive push notifications</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledToggleButton
              $active={preferences.notifications.push}
              onClick={() => handleNotificationToggle('push')}
            />
          </StyledSettingRow>

          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Achievement Notifications</StyledSettingLabel>
              <StyledSettingDescription>Get notified when you earn achievements</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledToggleButton
              $active={preferences.notifications.achievements}
              onClick={() => handleNotificationToggle('achievements')}
            />
          </StyledSettingRow>

          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Bill Reminders</StyledSettingLabel>
              <StyledSettingDescription>Receive reminders for upcoming bills</StyledSettingDescription>
            </StyledSettingInfo>
            <StyledToggleButton
              $active={preferences.notifications.bills}
              onClick={() => handleNotificationToggle('bills')}
            />
          </StyledSettingRow>
        </StyledSettingCard>
      </StyledSection>

      <StyledSection>
        <StyledSectionTitle>
          <Shield size={20} />
          {t('settings.security')}
        </StyledSectionTitle>
        <StyledSettingCard>
          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Change Password</StyledSettingLabel>
              <StyledSettingDescription>Update your account password</StyledSettingDescription>
            </StyledSettingInfo>
            <Button variant='outline' size='sm'>
              Change Password
            </Button>
          </StyledSettingRow>

          <StyledSettingRow>
            <StyledSettingInfo>
              <StyledSettingLabel>Two-Factor Authentication</StyledSettingLabel>
              <StyledSettingDescription>Add an extra layer of security</StyledSettingDescription>
            </StyledSettingInfo>
            <Button variant='outline' size='sm'>
              Enable 2FA
            </Button>
          </StyledSettingRow>
        </StyledSettingCard>
      </StyledSection>

      <StyledButtonGroup>
        <Button variant='outline'>{t('common.cancel')}</Button>
        <Button onClick={handleSave}>{t('common.save')}</Button>
      </StyledButtonGroup>
    </StyledSettingsContainer>
  );
};

export default SettingsPage;
